import React, { useEffect, useRef, useState } from 'react';
import { farZoomNow, onFarZoomChange } from '../utils/farZoom';
import { measureRound, readLoading, startRound, type LoadingRound, type MediaLookups } from '../utils/mediaProgress';

interface MediaLoadingBarProps {
  /** The canvas's content layer, where its cards are */
  getLayer: () => HTMLElement | null;
  /** The lookups of the canvas's pictures (App gives the same to the canvas through MediaLookupsContext) */
  lookups: MediaLookups;
}

/** Nothing shows for loading that is over this soon: a picture added to a loaded workspace does not flash a bar */
export const SHOW_AFTER_MS = 400;
/** "Images loaded" stays this long, then the bar goes */
const DONE_FOR_MS = 900;
/** With no picture arriving for this long the bar goes (something that never answers); it comes back when one does */
const STALLED_AFTER_MS = 30_000;
/** Measured at most this often while things change */
const MEASURE_EVERY_MS = 100;

type Shown = { phase: 'hidden' } | { phase: 'loading' | 'done'; fraction: number };

const sameShown = (a: Shown, b: Shown) => a.phase === b.phase && (a.phase === 'hidden' || (b.phase !== 'hidden' && Math.round(a.fraction * 100) === Math.round(b.fraction * 100)));

/**
 * A small bar in the top bar while the canvas's pictures load: how far they are, then "Images
 * loaded" (utils/mediaProgress.ts counts). It reads the canvas's content layer only when
 * something in it changed, ten times a second at most, and draws nothing on the canvas.
 */
export const MediaLoadingBar: React.FC<MediaLoadingBarProps> = ({ getLayer, lookups }) => {
  const [shown, setShownState] = useState<Shown>({ phase: 'hidden' });
  // Measured ten times a second while loading: drawn again only when what shows changes
  const setShown = (next: Shown) => setShownState(prev => (sameShown(prev, next) ? prev : next));
  const getLayerRef = useRef(getLayer);
  getLayerRef.current = getLayer;

  useEffect(() => {
    let round: LoadingRound | null = null;
    let lastDone = -1;
    let lastProgressAt = 0;
    let measureTimer: ReturnType<typeof setTimeout> | null = null;
    let stallTimer: ReturnType<typeof setTimeout> | null = null;
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const later = (ms: number, work: () => void) => {
      const timer = setTimeout(() => { timers.delete(timer); work(); }, ms);
      timers.add(timer);
    };
    let layer: HTMLElement | null = null;
    let observer: MutationObserver | null = null;

    const measure = () => {
      measureTimer = null;
      if (!layer) return;
      const far = farZoomNow();
      const now = readLoading(layer, far);
      const counts = lookups.counts();
      if (!round) {
        if (!now.images.length && !now.waiting.length && counts.started === counts.ended) return;
        lookups.restart();
        round = startRound(Date.now());
        lastDone = -1;
        // Shown only if it is still loading by then
        later(SHOW_AFTER_MS, schedule);
      }
      const { done, total, pending } = measureRound(round, now, lookups.counts(), far);
      const at = Date.now();
      if (!pending) {
        const wasShown = at - round.startedAt >= SHOW_AFTER_MS;
        round = null;
        if (!wasShown) return;
        setShown({ phase: 'done', fraction: 1 });
        later(DONE_FOR_MS, () => { if (!round) setShown({ phase: 'hidden' }); });
        return;
      }
      if (done !== lastDone) {
        lastDone = done;
        lastProgressAt = at;
        if (stallTimer !== null) clearTimeout(stallTimer);
        stallTimer = setTimeout(() => { stallTimer = null; schedule(); }, STALLED_AFTER_MS + 10);
      }
      const showing = at - round.startedAt >= SHOW_AFTER_MS && at - lastProgressAt < STALLED_AFTER_MS;
      setShown(showing ? { phase: 'loading', fraction: total ? done / total : 0 } : { phase: 'hidden' });
    };
    const schedule = () => {
      if (measureTimer === null) measureTimer = setTimeout(measure, MEASURE_EVERY_MS);
    };

    // The content layer is there once the canvas is; looked for again until it is
    const attach = () => {
      layer = getLayerRef.current();
      if (!layer) {
        later(500, attach);
        return;
      }
      observer = new MutationObserver(schedule);
      observer.observe(layer, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'data-far-picture'] });
      // Neither event bubbles: heard on the way down
      layer.addEventListener('load', schedule, true);
      layer.addEventListener('error', schedule, true);
      schedule();
    };
    attach();
    const stopLookups = lookups.subscribe(schedule);
    const stopFar = onFarZoomChange(schedule);

    return () => {
      observer?.disconnect();
      layer?.removeEventListener('load', schedule, true);
      layer?.removeEventListener('error', schedule, true);
      stopLookups();
      stopFar();
      if (measureTimer !== null) clearTimeout(measureTimer);
      if (stallTimer !== null) clearTimeout(stallTimer);
      timers.forEach(clearTimeout);
    };
  }, [lookups]);

  if (shown.phase === 'hidden') return null;
  const percent = Math.round(shown.fraction * 100);
  const done = shown.phase === 'done';
  return (
    <div
      role="progressbar"
      aria-label={done ? 'Images loaded' : 'Loading images'}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      className="ns-glass ns-glass-strong h-10 max-w-full min-w-0 flex items-center gap-2.5 px-4 rounded-full"
      data-media-loading={shown.phase}
    >
      <span className="ns-t-small text-ink whitespace-nowrap hidden sm:inline">{done ? 'Images loaded' : 'Loading images'}</span>
      {/* ui-ok: a progress track is a pill, not a circle */}
      <span className="relative h-1 w-20 shrink-0 rounded-full bg-cream-300 overflow-hidden" aria-hidden="true">
        <span
          className="absolute inset-0 origin-left rounded-full bg-lavender-deep transition-transform duration-300 ease-snap"
          style={{ transform: `scaleX(${shown.fraction})` }}
        />
      </span>
      <span className="ns-t-meta tabular-nums whitespace-nowrap" aria-hidden="true">{percent}%</span>
    </div>
  );
};

export default MediaLoadingBar;
